/**
 * Analysis summary: counts, overall SLA, mission SLA and antenna utilisation
 * lists, recommendations and the saturation verdict. Rows select the mission
 * or antenna so the globe and the detail tab follow. Every coloured figure
 * carries an icon or a word as well; spacing and the list font follow the
 * density setting.
 */
import { useMemo } from 'react';
import { AlertTriangle, ArrowRight, Gauge, Lightbulb } from 'lucide-react';
import { useUI } from '@/state/store';
import { Chip, StatusIcon } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useLoader } from '../../store';
import { fmtMinutes, pct, slaTone, utilTone } from '../format';

const TONE_WORD = { nominal: 'on target', caution: 'below target', warning: 'well below target' } as const;
const UTIL_WORD = { nominal: 'normal', caution: 'high', warning: 'overloaded' } as const;

function Bar({ value, tone }: { value: number; tone: 'nominal' | 'caution' | 'warning' }) {
  const color = tone === 'nominal' ? 'bg-nominal' : tone === 'caution' ? 'bg-caution' : 'bg-warning';
  return (
    <div aria-hidden className="h-1.5 w-full overflow-hidden rounded-full bg-inset">
      <div
        className={cn('h-full rounded-full transition-[width]', color)}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'warning' | 'caution' | 'nominal';
}) {
  return (
    <div className="rounded-control border border-hairline bg-inset/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{label}</div>
      <div
        className={cn(
          'flex items-center gap-1.5 font-mono text-lg text-ink',
          tone === 'warning' && 'text-warning',
          tone === 'caution' && 'text-caution',
        )}
      >
        {value}
        {tone && tone !== 'nominal' && <StatusIcon tone={tone} label={tone === 'warning' ? 'needs action' : 'attention'} className="h-3.5 w-3.5" />}
      </div>
    </div>
  );
}

export function SummaryPanel() {
  const dataset = useLoader((s) => s.dataset);
  const results = useLoader((s) => s.results);
  const status = useLoader((s) => s.analysisStatus);
  const select = useUI((s) => s.select);

  const missionRows = useMemo(() => {
    if (!results || !dataset) return [];
    return Object.entries(results.missionSLA)
      .map(([id, sla]) => ({ id, sla, mission: dataset.missions.find((m) => m.id === id) }))
      .sort((a, b) => a.sla.percentage - b.sla.percentage || a.id.localeCompare(b.id));
  }, [results, dataset]);

  const antennaRows = useMemo(() => {
    if (!results || !dataset) return [];
    return Object.entries(results.antennaUtil)
      .map(([id, util]) => ({ id, util, antenna: dataset.antennas.find((a) => a.id === id) }))
      .sort((a, b) => b.util.percentage - a.util.percentage || a.id.localeCompare(b.id));
  }, [results, dataset]);

  if (!results) {
    return (
      <div className="p-4 text-xs text-ink-3">
        {status === 'running'
          ? 'Analysis running.'
          : status === 'error'
            ? 'The last analysis failed; see the message on the stage.'
            : 'No analysis yet. Press R or Run.'}
      </div>
    );
  }

  const s = results.summary;
  const overallTone = s.overallSLA >= 95 ? 'nominal' : s.overallSLA >= 70 ? 'caution' : 'warning';

  return (
    <div className="flex flex-col gap-section p-panel text-data">
      <div className="grid grid-cols-2 gap-2">
        <Stat label="Missions" value={s.missionCount} />
        <Stat label="Antennas" value={s.antennaCount} />
        <Stat
          label="Conflicts"
          value={s.totalConflicts}
          tone={s.totalConflicts > 0 ? 'caution' : undefined}
        />
        <Stat
          label="Unresolved"
          value={s.unresolvedConflicts}
          tone={s.unresolvedConflicts > 0 ? 'warning' : undefined}
        />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-ink-3">Overall SLA</span>
          <span
            className={cn(
              'flex items-center gap-1 font-mono text-sm',
              overallTone === 'nominal'
                ? 'text-nominal'
                : overallTone === 'caution'
                  ? 'text-caution'
                  : 'text-warning',
            )}
          >
            <StatusIcon tone={overallTone} label={TONE_WORD[overallTone]} className="h-3.5 w-3.5" />
            {pct(s.overallSLA)}
          </span>
        </div>
        <Bar value={s.overallSLA} tone={overallTone} />
      </div>

      {results.saturation && (
        <div className="flex items-start gap-2 rounded-control border border-hairline bg-inset/60 px-3 py-2">
          <Gauge className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-2" />
          <div className="leading-snug text-ink-2">
            <div className="text-ink">{results.saturation.recommendation}</div>
            <div className="mt-0.5 text-ink-3">
              Minimum SLA {pct(results.saturation.minimumSLA)}, {results.saturation.missionsAboveTarget} of{' '}
              {results.saturation.totalMissions} missions above target
              {results.saturation.bottleneckAntenna && (
                <>
                  , bottleneck <span className="font-mono">{results.saturation.bottleneckAntenna}</span> at{' '}
                  {pct(results.saturation.bottleneckUtil)}
                </>
              )}
              .
            </div>
          </div>
        </div>
      )}

      {results.recommendations.length > 0 && (
        <div>
          <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-ink-3">
            <Lightbulb className="h-3 w-3" /> Recommendations
          </div>
          <ul className="flex flex-col gap-1">
            {results.recommendations.map((r) => (
              <li
                key={`${r.missionId}-${r.antennaId}`}
                className="rounded-control border border-hairline px-2.5 py-cell-y leading-snug text-ink-2"
              >
                <button
                  type="button"
                  className="text-ink hover:text-accent"
                  onClick={() => select({ missionId: r.missionId, antennaId: null, assetId: null })}
                >
                  {r.missionShortName}
                </button>{' '}
                at {pct(r.currentSLA)}: add <span className="font-mono text-ink">{r.antennaShortName}</span> (
                {pct(r.currentUtil)} used)
                <span className="text-nominal"> +{r.estimatedImprovement} pts</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Mission SLA</div>
        <ul className="flex flex-col gap-1">
          {missionRows.map(({ id, sla, mission }) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => select({ missionId: id, antennaId: null, assetId: null })}
                className="flex w-full items-center gap-2 rounded-control px-2 py-cell-y text-left hover:bg-overlay"
              >
                <span className="w-20 shrink-0 truncate text-ink">{mission?.shortName ?? id}</span>
                <div className="flex-1">
                  <Bar value={sla.percentage} tone={slaTone(sla.status)} />
                </div>
                <span className="w-10 shrink-0 text-right font-mono text-ink-2">{pct(sla.percentage)}</span>
                <Chip tone={slaTone(sla.status)} className="w-14 justify-center">
                  {sla.status}
                </Chip>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Antenna utilisation</div>
        <ul className="flex flex-col gap-1">
          {antennaRows.map(({ id, util, antenna }) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => select({ antennaId: id, missionId: null, assetId: null })}
                className="flex w-full items-center gap-2 rounded-control px-2 py-cell-y text-left hover:bg-overlay"
              >
                <span className="w-20 shrink-0 truncate text-ink">{antenna?.shortName ?? id}</span>
                <div className="flex-1">
                  <Bar value={util.percentage} tone={utilTone(util.status)} />
                </div>
                <span className="flex w-14 shrink-0 items-center justify-end gap-1 font-mono text-ink-2">
                  {utilTone(util.status) !== 'nominal' && (
                    <StatusIcon tone={utilTone(util.status)} label={UTIL_WORD[utilTone(util.status)]} />
                  )}
                  {pct(util.percentage)}
                </span>
                <span className="w-14 shrink-0 text-right text-ink-3" title="Scheduled minutes">
                  {fmtMinutes(util.scheduledMinutes)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {results.conflicts.length > 0 && (
        <div className="flex items-center gap-2 text-ink-3">
          <AlertTriangle aria-hidden className="h-3.5 w-3.5 text-caution" />
          {results.conflicts.length} conflicts logged
          <button
            type="button"
            className="ml-auto flex items-center gap-1 text-ink-2 hover:text-ink"
            onClick={() => useUI.getState().setBottomTab('conflicts')}
          >
            Open log <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
